/**
 * Compact "which Google Ads account are we acting on" line, shown at the top of
 * the Ads-related onboarding cards (conversions / conversion labels / verify).
 *
 * Reads the account name + customer ID from account_settings (written by the
 * ads_conversions step). The name is only present after that step has run; until
 * then it falls back to the formatted customer ID, and renders nothing if
 * neither is set yet. The shared queryKey lets react-query dedupe the fetch
 * across the three cards.
 */
import { useQuery } from '@tanstack/react-query'
import { Megaphone } from 'lucide-react'
import { supabase } from '@/lib/supabase'

/** Format a 10-digit Ads customer ID as xxx-xxx-xxxx (Google's display form). */
function formatCustomerId(id: string): string {
  const digits = id.replace(/\D/g, '')
  return digits.length === 10
    ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    : id
}

export function GoogleAdsAccountBadge({ accountId }: { accountId: string }) {
  const { data } = useQuery({
    queryKey: ['google-ads-account', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', accountId)
        .in('key', ['google_ads_account_name', 'google_ads_customer_id'])
      if (error) throw new Error(error.message)
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.value != null) map[row.key] = row.value
      }
      return map
    },
  })

  const name = data?.['google_ads_account_name']
  const id = data?.['google_ads_customer_id']
  if (!name && !id) return null

  return (
    <div className="mb-3 flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
      <Megaphone className="h-3.5 w-3.5 shrink-0" />
      <span>Google Ads:</span>
      {name && <span className="font-medium text-foreground">{name}</span>}
      {id && <span className="font-mono">{formatCustomerId(id)}</span>}
    </div>
  )
}
